import {
  formatCredentialTimestamp,
  formatDate,
} from "../../../lib/format-date";
import { stateDisplayName } from "../state-names";
import type { RunReport } from "../types";

export type SourceCheck = RunReport["checks"][number];
type StateSource = SourceCheck["stateSources"][number];
type AssessmentItem = SourceCheck["assessment"]["items"][number];
export type StateRow = { source: StateSource; item?: AssessmentItem };

export const NO_RESULT_PLACEHOLDER = "—";

/** Every state the shared check screened, joined to that state's own
 * assessment item. Sorted by full state name so lists read alphabetically.
 * Empty until the check has collected. */
export function stateRows(check: SourceCheck): StateRow[] {
  return [...check.stateSources]
    .map((source) => ({
      source,
      item: check.assessment.items.find(
        (item) => item.resultKey === source.resultKey,
      ),
    }))
    .sort((a, b) =>
      stateDisplayName(a.source.stateCode).localeCompare(
        stateDisplayName(b.source.stateCode),
      ),
    );
}

/** The state's list was not current when the check ran, so it has no sealed
 * snapshot; the table shows a dash instead of a stale or invented date. */
export function publishedAtLabel(source: StateSource) {
  if (source.unavailable) {
    return NO_RESULT_PLACEHOLDER;
  }
  return source.publishedAt
    ? formatDate(source.publishedAt)
    : "Not published by state";
}

export function confirmedAtLabel(source: StateSource) {
  return source.unavailable || !source.confirmedAt
    ? NO_RESULT_PLACEHOLDER
    : formatCredentialTimestamp(source.confirmedAt);
}

export function recordCountLabel(source: StateSource) {
  return source.unavailable || source.recordCount === null
    ? NO_RESULT_PLACEHOLDER
    : source.recordCount.toLocaleString();
}

/** A single state's detail is presented as that state's own list and result
 * instead of the shared check's overall status. */
export function stateCheckView({
  check,
  source,
  item,
}: {
  check: SourceCheck;
  source: StateSource;
  item?: AssessmentItem;
}): SourceCheck {
  return {
    ...check,
    authorityName: source.label,
    assessment: {
      status: item?.status ?? check.assessment.status,
      label: item?.label ?? check.assessment.label,
      description: item?.description ?? check.assessment.description,
      items: item ? [item] : [],
    },
  };
}

export function stateNames(rows: StateRow[]) {
  return rows.map(({ source }) => stateDisplayName(source.stateCode));
}

export function pluralizeStates(count: number) {
  return `${count} ${count === 1 ? "state" : "states"}`;
}
